import { useState, useMemo } from 'react';
import { Message, QueuedMessage } from '../../../types/Messaging/Message';
import dayjs from 'dayjs';
import { RootState } from '../../../store/store';
import { useSelector } from 'react-redux';

export const useDmMessages = (conversation_id?: number) => {
  const userProfile = useSelector((state: RootState) => state.userProfile);

  //================================= MESSAGES ===================================================
  const [newMessages, setNewMessages] = useState<Map<number, Message>>(
    new Map()
  );
  const newMessagesArray = useMemo(
    () => Array.from(newMessages.values()),
    [newMessages]
  );

  const saveToMessage = (message: Message) => {
    setNewMessages((prev) => {
      const next = new Map(prev);
      const normalized = { ...message, readBy: (message as any).reads ?? [] };
      const existing = next.get(message.id);
      if (existing) {
        next.set(message.id, { ...existing, ...normalized });
      } else {
        next.set(message.id, normalized);
      }
      return next;
    });
  };

  const updateReadMessage = (data: {
    messageId: number;
    userId: number;
    roleId: number;
    readAt?: string;
  }) => {
    setNewMessages((prev) => {
      const next = new Map(prev);

      const message = next.get(data.messageId);
      if (message) {
        const updatedMessage = {
          ...message,
          reads: [
            ...message.reads,
            {
              id: data.messageId,
              message_id: message.id,
              read_at: data.readAt,
              role_id: data.roleId,
              user_id: data.userId,
            } as Message['reads'][number],
          ],
        };
        next.set(data.messageId, updatedMessage);
      }
      return next;
    });
  };
  //==============================================================================================

  //================================= QUEUED MESSAGES ============================================
  const [queuedMsg, setQueuedMsg] = useState<Map<string, QueuedMessage>>(
    new Map()
  );
  const queuedMessagesArray = useMemo(
    () => Array.from(queuedMsg.values()),
    [queuedMsg]
  );

  const saveToQueue = (messageId: string, message: string) => {
    if (!userProfile || !conversation_id) return;
    const newQueueMsg = {
      id: messageId,
      conversation_id,
      sender_role_id: userProfile?.role.id,
      isQueued: true,
      type: 'text' as const,
      body: message,
      isSent: false,
      isSending: true,
      isError: false,
      created_at: dayjs().utc().toISOString(),
    };

    setQueuedMsg((prev) => {
      const newState = new Map(prev);
      newState.set(messageId, newQueueMsg);
      return newState;
    });
  };

  const updateQueue = (messageId: string, updates?: Partial<QueuedMessage>) =>
    setQueuedMsg((prev) => {
      const newState = new Map(prev);
      if (updates) {
        const target = newState.get(messageId);
        if (target) {
          newState.set(messageId, {
            ...target,
            ...updates,
          });
        }
      } else newState.delete(messageId);
      return newState;
    });

  //TIMEOUT FOR FAILED MESSAGES
  const setMessageTimeout = (queuedMsgId: string) => {
    // start a 3-minute timeout to mark the message as failed if still sending
    const MESSAGE_FAILED_TIMEOUT_MS = 30 * 1000;

    setTimeout(() => {
      updateQueue(queuedMsgId, {
        isSending: false,
        isError: true,
        isSent: false,
      });
    }, MESSAGE_FAILED_TIMEOUT_MS);
  };

  const getQueuedMessage = (messageId: string) => queuedMsg.get(messageId);

  //===============================================================================================
  return {
    saveToMessage,
    queue: queuedMessagesArray,
    __saveToRawMessageMap: setNewMessages,
    __rawQueueMap: queuedMsg,
    queueMessage: saveToQueue,
    updateQueue,
    __saveToRawQueueMap: setQueuedMsg,
    getQueuedMessage,
    setMessageTimeout,
    updateReadMessage,
    messages: newMessagesArray,
    __rawMessagesMap: newMessages,
  };
};
